<script setup lang="ts">
import {computed, ref} from "vue";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import Translator from "@/utils/common/Translator.ts";

let props = defineProps({
  src: {
    type: String,
    required: true
  },
  fileName: {
    type: String,
    required: false
  }
})

const fileVisible = defineModel<boolean>()
const textContent = ref('');
const textDirection = ref<string>(Translator.fetchDir());
const mimeTypes: Record<string, string> = {
  // Images
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
  ico: 'image/vnd.microsoft.icon',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  avif: 'image/avif',

  // Videos
  mp4: 'video/mp4',
  webm: 'video/webm',
  ogv: 'video/ogg',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  m4v: 'video/x-m4v',

  // Audio
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  m4a: 'audio/mp4',
  flac: 'audio/flac',
  aac: 'audio/aac',

  // Documents
  pdf: 'application/pdf',
  // doc: 'application/msword',
  // docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // xls: 'application/vnd.ms-excel',
  // xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // ppt: 'application/vnd.ms-powerpoint',
  // pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  csv: 'text/csv',
  // rtf: 'application/rtf',
  md: 'text/markdown',
  html: 'text/html',
  htm: 'text/html',
  json: 'application/json',
  xml: 'application/xml',

};

const srcType = computed(() => getFileType(props.fileName));

function getFileType(fileName: string | undefined): string | undefined {
  const strArr: string[] = fileName?.split('.') ?? [];
  const extension = strArr[strArr.length - 1]?.toLowerCase();
  if (extension && mimeTypes[extension]) {
    return mimeTypes[extension];
  }
  return;
}

function isVideo(): boolean {
  return srcType.value?.startsWith('video') ?? false;
}

function isAudio(): boolean {
  return srcType.value?.startsWith('audio') ?? false;
}

function isPdf(): boolean {
  return srcType.value === 'application/pdf';
}

function isText(): boolean {
  if (srcType.value?.startsWith('text') || ObjectChecker.isAnyEqualToFirst(srcType.value, 'application/json', 'application/xml')) {
    fetchTextContent();
    return true;
  }
  return false;
}

function isImage(): boolean {
  if (ObjectChecker.isNullOrUndefined(srcType.value))
    return true;
  return srcType.value.startsWith('image');
}

function getPdfUrl() {
  return `${props.src}&purpose=${DownloadPurpose.THUMBNAIL}`;
}

async function fetchTextContent() {
  try {
    const res = await fetch(props.src);
    textContent.value = await res.text();
  } catch (e) {
    textContent.value = 'Failed to load text file.'
  }
}

function toggleTextDirection() {
  textDirection.value = textDirection.value === Translator.LTR ? Translator.RTL : Translator.LTR;
}
</script>
<template>
  <q-dialog v-model="fileVisible">
    <q-card class="tw-max-w-[80vw] tw-max-h-[90vh] tw-bg-transparent">
      <q-card-section class="tw-p-0 tw-max-h-full tw-relative">
        <video v-if="isVideo()" class="tw-w-fit" controls>
          <source :src="src" :type="srcType">
        </video>
        <audio v-else-if="isAudio()" controls :src="src"></audio>
        <div v-else-if="isText()" class="tw-relative tw-w-full tw-h-full tw-p-4">
          <q-btn
            @click="toggleTextDirection"
            :icon="textDirection === 'ltr' ? 'format_textdirection_r_to_l' : 'format_textdirection_l_to_r'"
            round
            dense
            size="sm"
            color="primary"
            class="tw-sticky tw-top-9 tw-right-2 tw-z-10"
            :title="textDirection === 'ltr' ? 'Switch to RTL' : 'Switch to LTR'"
          />
          <pre :alt="fileName" v-html="textContent" :dir="textDirection"
               class="tw-w-full tw-h-full tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark tw-text-black dark:tw-text-white tw-p-4 tw-rounded-md tw-overflow-auto tw-whitespace-pre-wrap"/>
        </div>
        <embed v-else-if="isPdf()" :src="getPdfUrl()" type="application/pdf" class="tw-w-[70vw] tw-h-[80vh]"/>
        <img v-else-if="isImage()" :src="src" class="tw-h-[90vh] tw-max-h-fit" :alt="fileName"/>
        <div v-else class="tw-w-full tw-h-[100px] tw-content-center tw-text-center tw-rounded-md">
          <span v-text="Translator.translateId('Preview Not Available. (Download file to view)')"
                class="tw-bg-namaBackground-deep-dark dark:tw-bg-namaBackground-deep-light tw-text-white dark:tw-text-black tw-p-10 tw-overflow-none tw-rounded-md"/>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style scoped>

</style>
